import React, { ReactNode, useCallback, useEffect } from 'react';

import {
  EmailOutlined,
  Link,
  LockOpenOutlined,
  PlayCircleOutlined,
  RadioButtonChecked,
} from '@mui/icons-material';
import {
  Button,
  IconButton,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';

import {
  IEvent,
  IEventTypeObject,
  IPartyTransactionEvent,
} from '../../../types/Events';
import { ISigner } from '../../../types/Parties';
import { black, successGreen, warningRed } from '../../constants/colors';
import {
  SignerType,
  isSignerOfType,
  signerTypeDisplayStrings,
} from '../../constants/solexSignerTypes';
import { getPartyName } from '../../utils/helpers';

export interface IOrderTrackerRow {
  displayUnlock?: boolean | undefined;
  unlockParty: (partyId: string) => void;
  unlockSession: (partyId: string) => void;
  party: ISigner;
  signingDelegatorParty?: ISigner;
  includeSequenceInLabel: boolean;
  events: IEvent[];
  isIPEN: boolean;
  isActiveSession?: boolean;
  orderStatus?: string | null;
  isActiveSigner?: boolean;
}

export default function OrderTrackerRow(props: IOrderTrackerRow) {
  const {
    events,
    isIPEN,
    party,
    signingDelegatorParty,
    includeSequenceInLabel,
    displayUnlock,
    isActiveSession,
    isActiveSigner,
    orderStatus,
    unlockParty,
    unlockSession,
  } = props;

  const isSigningDelegated = !!party.SigningDelegatePartyId;
  const isLender = isSignerOfType(party, SignerType.Lender);
  const appendSequence = (label: string, sequence: number | undefined): string =>
    includeSequenceInLabel && sequence && sequence > 0
      ? `${label} ${sequence}`
      : label;

  const parseEvents = useCallback(() => {
    const eventArray: IEvent[] =
      Array.isArray(events) && events?.length > 0 ? events : [];
    const allEvents: any = {} as IEventTypeObject;
    for (let i = 0; i < eventArray.length; i++) {
      switch (eventArray[i].Name) {
        case 'Documents Distributed':
          allEvents.link = eventArray[i];
          break;
        case 'Open, Links Sent':
          allEvents.link = eventArray[i];
          break;
        case 'Authenticate':
          allEvents.auth = Object.assign({}, eventArray[i], {
            isFailure: false,
          });
          break;
        case 'Authenticate, Unlocked': // this is not a real pushback - this is only to simulate unlocked state
          allEvents.auth = Object.assign({}, eventArray[i], {
            isFailure: false,
          });
          break;
        case 'Authenticate, Failed':
          allEvents.auth = Object.assign({}, eventArray[i], {
            isFailure: true,
          });
          if (party.SolexSigner?.IsSessionLocked) {
            allEvents.auth = Object.assign(
              {},
              { ...allEvents.auth },
              { locked: true }
            );
          }
          break;
        case 'Consent':
          allEvents.consent = eventArray[i];
          break;
        case 'Consent, Failed':
          allEvents.auth = Object.assign({}, eventArray[i], {
            isFailure: false,
          });
          break;
        case 'Sign':
          allEvents.sign = eventArray[i];
          break;
        case 'Open, All Signed':
          allEvents.allSigned = eventArray[i];
          break;
        default:
          break;
      }
    }
    return allEvents;
  }, [events]);

  const [userEvents, setEvents] = React.useState(parseEvents);

  useEffect(() => {
    setEvents(parseEvents);
  }, [party, setEvents, parseEvents]);

  const getSignStatus = (event: IEvent) => {
    if (event) {
      return 'Completed';
    } else {
      return 'Incomplete';
    }
  };

  const clickedEmail = () => {
    if (Array.isArray(party?.Emails) && party?.Emails.length > 0) {
      window.location.href = 'mailto:' + party?.Emails[0];
    }
  };

  const handleOpenPartyLink = (events: { link: IPartyTransactionEvent }) => {
    if (events.link && events.link.DocLink) {
      window.open(events.link.DocLink, '_blank');
    }
  };

  const isLinkDisabled = () => {
    let status = true;
    const signStatus = getSignStatus(userEvents?.sign);
    if (
      signStatus != 'Completed' &&
      orderStatus?.toLocaleLowerCase() === 'open, links sent'
    ) {
      //enable link
      status = false;
    }
    return status;
  };

  const unlockSessionLink = (partyId: string | undefined) => {
    if (partyId) {
      unlockSession(partyId);
    }
  };

  const unlockPartyLink = (partyId?: string) => {
    if (partyId) {
      unlockParty(partyId);
    }
  };

  const getSignerTypeLabel = (): string => {
    const signerType = party.SignerType?.toLowerCase() as SignerType;

    if (
      signingDelegatorParty &&
      isSignerOfType(party, SignerType.PowerOfAttorney)
    ) {
      const signingDelegatorSignerType =
        signingDelegatorParty.SignerType?.toLowerCase() as SignerType;
      const label = `${signerTypeDisplayStrings[signerType]} for ${signerTypeDisplayStrings[signingDelegatorSignerType]}`;
      return appendSequence(label, signingDelegatorParty.SolexSigner?.TypeSequence);
    }

    const label = signerTypeDisplayStrings[signerType];
    return appendSequence(label, party.SolexSigner?.TypeSequence);
  };

  const replaceWithDashIf = (condition: boolean, node: ReactNode): ReactNode =>
    condition ? <div style={{ textAlign: 'center' }}>-</div> : node;

  return (
    <>
      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
        <TableCell>
          <Typography sx={{ fontSize: '14px', fontWeight: '500' }}>
            {getSignerTypeLabel()}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontSize: '14px', fontWeight: '500' }}>
            {getPartyName(party)}
          </Typography>
        </TableCell>
        <TableCell className='customer-details'>
          {Array.isArray(party?.Emails) && party?.Emails.length > 0 && (
            <Button
              startIcon={<EmailOutlined />}
              size='small'
              style={{ textTransform: 'none' }}
              onClick={clickedEmail}>
              {party?.Emails[0] ? party?.Emails[0] : '-'}
            </Button>
          )}
        </TableCell>
        <TableCell>
          {replaceWithDashIf(
            isSigningDelegated,
            (Array.isArray(party?.Emails) && party?.Emails.length > 0) ||
              isIPEN ? (
              <div
                style={{
                  color:
                    getSignStatus(userEvents?.sign) === 'Incomplete'
                      ? warningRed
                      : successGreen,
                }}>
                {getSignStatus(userEvents?.sign)}
              </div>
            ) : (
              <div style={{ color: black }}>{'Ink Sign'}</div>
            )
          )}
        </TableCell>
        <TableCell style={{ textAlign: 'center' }}>
          {replaceWithDashIf(
            isSigningDelegated,
            <>
              {(Array.isArray(party?.Emails) && party?.Emails.length > 0) ||
              isIPEN ? (
                <IconButton
                  size='small'
                  disabled={isLinkDisabled()}
                  onClick={() => handleOpenPartyLink(userEvents)}
                  data-testid={`party-link-${party.SolexSigner?.Id}`}>
                  <Link />
                </IconButton>
              ) : (
                ''
              )}
            </>
          )}
        </TableCell>
        {displayUnlock && (
          <TableCell style={{ textAlign: 'center' }}>
            {replaceWithDashIf(
              isSigningDelegated,
              <>
                {(Array.isArray(party?.Emails) && party?.Emails.length > 0) ||
                isIPEN ? (
                  <IconButton
                    data-testid={`unlock-${party.SolexSigner?.Id}`}
                    size='small'
                    onClick={() => unlockPartyLink(party.Id)}
                    disabled={!party.SolexSigner?.IsSessionLocked}>
                    <LockOpenOutlined />
                  </IconButton>
                ) : (
                  ''
                )}
              </>
            )}
          </TableCell>
        )}
        {isIPEN && isActiveSession && (
          <TableCell style={{ textAlign: 'center' }}>
            {replaceWithDashIf(
              isSigningDelegated || isLender,
              <>
                {isActiveSigner ? (
                  <RadioButtonChecked
                    fontSize='medium'
                    sx={{ color: (theme) => theme.palette.success.light }}
                  />
                ) : (
                  <IconButton
                    size='small'
                    color='default'
                    role='radio'
                    onClick={() => unlockSessionLink(party.Id)}
                    disabled={!isActiveSession}>
                    <PlayCircleOutlined />
                  </IconButton>
                )}
              </>
            )}
          </TableCell>
        )}
      </TableRow>
    </>
  );
}
